import { useEffect, useState } from "react";
import { Box, Card, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { P } from "theme/typography";
import {
  GetDownloadUrlForImportAuditDocument,
  GetImportHistoryDocument,
  ImportAuditView,
  ImportType,
  SimpleLensView,
} from "generated/graphql";
import { useSnackbar } from "notistack";
import RequestStatus from "redux/slices/RequestStatus";
import { runGqlQuery } from "gqlClient";
import formatSizeBytes from "helpers/formatSizeBytes";
import { displayDateAndTime, displayMimeType, INTERNAL_USER_NAME, parseDate } from "Backend";
import PSelect from "components/PSelect";
import { LinkSX } from "theme";
import { useAppSelector } from "redux/hooks";
import { selectIsSupport } from "redux/slices/session";
import { selectUserById } from "redux/slices/client";
import { fullName } from "helpers/name-helpers";
import LensAutocomplete from "../../components/LensAutocomplete";
import { selectLenses } from "../../redux/slices/client";
import SkeletonRow from "../../components/SkeletonRow";
import { triggerDownloadWithUrl } from "helpers/triggerDownload";

interface ImportHistoryRowProps {
  audit: ImportAuditView;
  lenses: SimpleLensView[];
}

const ImportHistoryRow = (props: ImportHistoryRowProps) => {
  const { audit, lenses } = props;
  const auditUser = useAppSelector((state) => selectUserById(state, audit.authoredBy ?? 0));
  const lensName = lenses.find((l) => l.id === audit.lensId)?.name;
  const { enqueueSnackbar } = useSnackbar();

  const downloadAudit = async () => {
    try {
      const url = (await runGqlQuery(GetDownloadUrlForImportAuditDocument, { auditId: audit.id })).client
        .getDownloadUrlForImportAudit;

      triggerDownloadWithUrl(url);
      enqueueSnackbar("Your file will download shortly", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("There was an error downloading the audit file", { variant: "error" });
    }
  };

  return (
    <TableRow>
      <TableCell onClick={downloadAudit} sx={LinkSX}>
        {audit.fileName}
      </TableCell>
      <TableCell>{formatSizeBytes(audit.sizeBytes)}</TableCell>
      <TableCell>{displayMimeType(audit.contentType)}</TableCell>
      <TableCell>{audit.rowCount}</TableCell>
      <TableCell>{lensName}</TableCell>
      <TableCell>{displayDateAndTime(parseDate(audit.createdAt))}</TableCell>
      <TableCell title={auditUser?.email}>
        {auditUser !== undefined ? fullName(auditUser) : INTERNAL_USER_NAME}
      </TableCell>
    </TableRow>
  );
};

const ImportHistory = () => {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const [importType, setImportType] = useState<ImportType>(ImportType.Case);
  const [history, setHistory] = useState<ImportAuditView[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const isSupport = useAppSelector(selectIsSupport);
  const lenses = useAppSelector(selectLenses);
  const [selectedLens, setSelectedLens] = useState<SimpleLensView>();

  const loadHistory = (importType: ImportType, selectedLens?: SimpleLensView) => {
    setStatus(RequestStatus.PENDING);
    runGqlQuery(GetImportHistoryDocument, { importType, lensId: selectedLens?.id })
      .then((resp) => {
        setStatus(RequestStatus.SUCCEEDED);
        setHistory(resp.client.importAudits);
      })
      .catch(() => {
        setStatus(RequestStatus.FAILED);
        enqueueSnackbar("Operation failed", { variant: "error" });
      });
  };

  useEffect(() => {
    loadHistory(importType, selectedLens);
  }, [importType, selectedLens]);

  return (
    <Card sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", flexDirection: "column", p: 2, height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            "& > * + *": {
              marginLeft: 2,
            },
          }}>
          <P variant="body1" sx={{ fontSize: 24, width: 250 }}>
            Import Type
          </P>
          <PSelect value={importType} onChange={(evt) => setImportType(evt.target.value as ImportType)}>
            <MenuItem value={ImportType.Case}>Case</MenuItem>
            <MenuItem value={ImportType.Firm}>Firm</MenuItem>
            {isSupport && <MenuItem value={ImportType.Profile}>Profile</MenuItem>}
            {isSupport && <MenuItem value={ImportType.SectionValue}>Section Value</MenuItem>}
          </PSelect>
          <LensAutocomplete options={lenses} selectedLens={selectedLens} onChange={setSelectedLens} />
          <P variant="body4" sx={{ ml: 2 }}>
            Lens filter only currently works for Profile and Section imports
          </P>
        </Box>
        <Box sx={{ minHeight: 0 }}>
          <TableContainer sx={{ height: "100%" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>File Type </TableCell>
                  <TableCell>Rows</TableCell>
                  <TableCell>Lens</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Created By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status === RequestStatus.PENDING ? (
                  <>
                    <SkeletonRow columnNum={7} />
                    <SkeletonRow columnNum={7} />
                    <SkeletonRow columnNum={7} />
                    <SkeletonRow columnNum={7} />
                  </>
                ) : (
                  history.map((audit) => <ImportHistoryRow key={audit.id} audit={audit} lenses={lenses} />)
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Card>
  );
};

export default ImportHistory;
